import { useState, useEffect, useCallback } from 'react';
import PredefineItineraryContext from './PredefineItineraryContext';
import { predefineItineraryService } from '../../services/predefineItinerary/predefineItineraryService';
import { STANDARD_TRIP_TYPES, parseDurationString, createDefaultFormData } from '../../data/predefineItinerary/predefineItineraryData';
import { toast } from 'react-hot-toast';

export const PredefineItineraryState = (props) => {
  const [currentUserRole, setCurrentUserRole] = useState('admin');
  const [itineraries, setItineraries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [durationDays, setDurationDays] = useState('');
  const [durationNights, setDurationNights] = useState('');
  const [formData, setFormData] = useState(createDefaultFormData());

  const fetchItineraries = useCallback(async () => {
    const token = localStorage.getItem('token_agents');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await predefineItineraryService.getItineraries();
      if (res?.success) {
        setItineraries(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch itineraries:', error);
      toast.error('Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    const token = localStorage.getItem('token_agents');
    if (!token) return;
    try {
      const data = await predefineItineraryService.getLocations();
      const locList = Array.isArray(data) ? data : data?.data || [];
      setLocations(locList);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, []);

  useEffect(() => {
    // Eager fetching on root mount removed.
    // Itineraries will only be fetched on-demand when PredefinedItineraryPage mounts.
    const storedRole = localStorage.getItem('sub_user_role') || 'admin';
    setCurrentUserRole(storedRole);
    setLoading(false);
  }, []);

  const isRestricted = currentUserRole === 'bookings';

  const updateDurationState = useCallback((d, n) => {
    let combined = '';
    if (d && n) {
      combined = `${d} Days / ${n} Nights`;
    } else if (d) {
      combined = `${d} Days`;
    } else if (n) {
      combined = `${n} Nights`;
    }
    setFormData((prev) => ({ ...prev, duration: combined }));
  }, []);

  const resetForm = useCallback(() => {
    setDurationDays('');
    setDurationNights('');
    setFormData(createDefaultFormData());
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditingItem(null);
    setViewOnly(false);
    resetForm();
    setShowModal(true);
  }, [resetForm]);

  const handleOpenEdit = useCallback((item) => {
    setEditingItem(item);
    setViewOnly(false);
    const rawType =
      item.trrip_type ||
      (item.region ? item.region.charAt(0).toUpperCase() + item.region.slice(1) : 'Domestic');
    const isCustom = !STANDARD_TRIP_TYPES.includes(rawType);
    const parsedDur = parseDurationString(item.duration || '');
    setDurationDays(parsedDur.days);
    setDurationNights(parsedDur.nights);
    setFormData({
      itinerary_title: item.title || item.itinerary_title || '',
      location: item.location || item.destination_tag || item.destination || '',
      trrip_type: isCustom ? 'Custom' : rawType,
      custom_trrip_type: isCustom ? rawType : item.custom_trrip_type || '',
      duration: item.duration || '',
      description: item.description || item.itinerary_description || '',
      cover_image_url: item.cover_image_url || '',
      attachment_url: item.attachments?.[0]?.url || '',
      attachment_type: item.attachments?.[0]?.type || 'pdf',
      status: item.status || 'published',
      days: (item.days && item.days.length > 0 ? item.days : item.itinerary || []).map((d, idx) => {
        let day = d.day || `Day ${idx + 1}`;
        let title = d.title || '';
        if (!title && day.includes('-')) {
          const parts = day.split('-');
          day = parts[0].trim();
          title = parts.slice(1).join('-').trim();
        }
        return {
          day,
          title,
          pointers: d.pointers || d.remarks || ['', '', '']
        };
      })
    });
    setShowModal(true);
  }, []);

  const handleOpenView = useCallback((item) => {
    setEditingItem(item);
    setViewOnly(true);
    const rawType =
      item.trrip_type ||
      (item.region ? item.region.charAt(0).toUpperCase() + item.region.slice(1) : 'Domestic');
    const isCustom = !STANDARD_TRIP_TYPES.includes(rawType);
    const parsedDur = parseDurationString(item.duration || '');
    setDurationDays(parsedDur.days);
    setDurationNights(parsedDur.nights);
    setFormData({
      itinerary_title: item.title || item.itinerary_title || '',
      location: item.location || item.destination_tag || item.destination || '',
      trrip_type: isCustom ? 'Custom' : rawType,
      custom_trrip_type: isCustom ? rawType : item.custom_trrip_type || '',
      duration: item.duration || '',
      description: item.description || item.itinerary_description || '',
      cover_image_url: item.cover_image_url || '',
      attachment_url: item.attachments?.[0]?.url || '',
      attachment_type: item.attachments?.[0]?.type || 'pdf',
      status: item.status || 'published',
      days: (item.days && item.days.length > 0 ? item.days : item.itinerary || []).map((d, idx) => {
        let day = d.day || `Day ${idx + 1}`;
        let title = d.title || '';
        if (!title && day.includes('-')) {
          const parts = day.split('-');
          day = parts[0].trim();
          title = parts.slice(1).join('-').trim();
        }
        return {
          day,
          title,
          pointers: d.pointers || d.remarks || ['', '', '']
        };
      })
    });
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleFileUpload = useCallback(async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      setUploading(true);
      const res = await predefineItineraryService.uploadFile(uploadFormData);
      const fileUrl = res?.url || res?.fileUrl || res?.data?.url;
      if (type === 'cover') {
        setFormData((prev) => ({ ...prev, cover_image_url: fileUrl }));
      } else {
        setFormData((prev) => ({
          ...prev,
          attachment_url: fileUrl,
          attachment_type: file.type.includes('pdf')
            ? 'pdf'
            : file.type.includes('image')
            ? 'image'
            : 'doc'
        }));
      }
      toast.success('File uploaded successfully');
    } catch (err) {
      console.error('File upload error:', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  }, []);

  const addDay = useCallback(() => {
    setFormData((prev) => {
      const nextDays = [
        ...(prev.days || []),
        { day: `Day ${(prev.days?.length || 0) + 1}`, title: '', pointers: ['', '', ''] }
      ];
      const count = nextDays.length;
      const dStr = count.toString();
      const nStr = count > 1 ? (count - 1).toString() : '0';
      setDurationDays(dStr);
      setDurationNights(nStr);
      const autoDuration = `${dStr} Days / ${nStr} Nights`;
      return {
        ...prev,
        days: nextDays,
        duration: autoDuration
      };
    });
  }, []);

  const removeDay = useCallback((index) => {
    setFormData((prev) => {
      const nextDays = prev.days.filter((_, i) => i !== index);
      const count = nextDays.length;
      const dStr = count > 0 ? count.toString() : '1';
      const nStr = count > 1 ? (count - 1).toString() : '0';
      setDurationDays(dStr);
      setDurationNights(nStr);
      const autoDuration = count <= 1 ? `${dStr} Day` : `${dStr} Days / ${nStr} Nights`;
      return {
        ...prev,
        days: nextDays,
        duration: autoDuration
      };
    });
  }, []);

  const updateDayField = useCallback((index, field, value) => {
    setFormData((prev) => {
      const newDays = [...prev.days];
      newDays[index][field] = value;
      return { ...prev, days: newDays };
    });
  }, []);

  const addPointer = useCallback((dayIndex) => {
    setFormData((prev) => {
      const newDays = prev.days.map((d, i) => {
        if (i === dayIndex) {
          return {
            ...d,
            pointers: [...(d.pointers || []), '']
          };
        }
        return d;
      });
      return { ...prev, days: newDays };
    });
  }, []);

  const removePointer = useCallback((dayIndex, pointerIndex) => {
    setFormData((prev) => {
      const newDays = prev.days.map((d, i) => {
        if (i === dayIndex) {
          return {
            ...d,
            pointers: (d.pointers || []).filter((_, idx) => idx !== pointerIndex)
          };
        }
        return d;
      });
      return { ...prev, days: newDays };
    });
  }, []);

  const updatePointer = useCallback((dayIndex, pointerIndex, value) => {
    setFormData((prev) => {
      const newDays = prev.days.map((d, i) => {
        if (i === dayIndex) {
          const newPointers = [...(d.pointers || [])];
          newPointers[pointerIndex] = value;
          return {
            ...d,
            pointers: newPointers
          };
        }
        return d;
      });
      return { ...prev, days: newDays };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e, saveStatus = null) => {
      if (e) e.preventDefault();
      const submitStatus = saveStatus || formData.status;

      if (!formData.itinerary_title || !formData.description || !formData.duration?.trim()) {
        toast.error('Itinerary title, duration, and description are required');
        return;
      }

      if (formData.trrip_type === 'Custom' && !formData.custom_trrip_type?.trim()) {
        toast.error('Please specify custom trip type');
        return;
      }

      const finalType =
        formData.trrip_type === 'Custom' ? formData.custom_trrip_type.trim() : formData.trrip_type;

      const payload = {
        title: formData.itinerary_title,
        itinerary_title: formData.itinerary_title,
        description: formData.description,
        location: formData.location,
        destination_tag: formData.location,
        destination: formData.location,
        trrip_type: finalType,
        custom_trrip_type: formData.custom_trrip_type,
        region: finalType?.toLowerCase().includes('inter') ? 'international' : 'domestic',
        duration: formData.duration,
        cover_image_url: formData.cover_image_url,
        status: submitStatus,
        days: formData.days,
        itinerary: formData.days?.map((d) => ({
          day: d.day,
          date: d.date,
          remarks: d.pointers
        })),
        attachments: formData.attachment_url
          ? [
              {
                url: formData.attachment_url,
                type: formData.attachment_type,
                filename: `${formData.itinerary_title} Itinerary.${
                  formData.attachment_url.split('.').pop() || 'pdf'
                }`
              }
            ]
          : []
      };

      try {
        if (editingItem) {
          await predefineItineraryService.updateItinerary(editingItem._id, payload);
          toast.success('Itinerary updated successfully');
        } else {
          await predefineItineraryService.createItinerary(payload);
          toast.success('Itinerary created successfully');
        }
        setShowModal(false);
        fetchItineraries();
      } catch (error) {
        console.error('Error saving itinerary:', error);
        toast.error(error.response?.data?.message || 'Failed to save itinerary');
      }
    },
    [formData, editingItem, fetchItineraries]
  );

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm('Are you sure you want to delete this itinerary?')) return;
      try {
        await predefineItineraryService.deleteItinerary(id);
        toast.success('Itinerary deleted');
        fetchItineraries();
      } catch (error) {
        toast.error('Failed to delete itinerary');
      }
    },
    [fetchItineraries]
  );

  return (
    <PredefineItineraryContext.Provider
      value={{
        currentUserRole,
        setCurrentUserRole,
        itineraries,
        setItineraries,
        locations,
        setLocations,
        loading,
        setLoading,
        showModal,
        setShowModal,
        editingItem,
        setEditingItem,
        viewOnly,
        setViewOnly,
        uploading,
        setUploading,
        durationDays,
        setDurationDays,
        durationNights,
        setDurationNights,
        formData,
        setFormData,
        isRestricted,
        fetchItineraries,
        fetchLocations,
        updateDurationState,
        resetForm,
        handleOpenCreate,
        handleOpenEdit,
        handleOpenView,
        handleCloseModal,
        handleFileUpload,
        addDay,
        removeDay,
        updateDayField,
        addPointer,
        removePointer,
        updatePointer,
        handleSubmit,
        handleDelete
      }}
    >
      {props.children}
    </PredefineItineraryContext.Provider>
  );
};

export const ItineraryState = PredefineItineraryState;
export default PredefineItineraryState;
